import { useCallback, useContext, useEffect } from 'react'
import useMediaSourceViews from '../actions/useMediaSourceViews'
import { classifyAudioInputs, isDefinedDeviceId } from '../Logic'
import { MediaPrefilghtStatusState, MediaPreflightStatus, UserContext, UserContextType } from '../UserContext'
import ElectronIPC from './ElectronIPC'
import { env } from './EnvironmentVariables'
import { FeatureFlags } from './FeatureFlags'
import {
    isMuseAudioShareInputDevice,
    isMuseDAWBridgeDevice,
    isMuseRecordingDevice,
    isSystemAudio,
} from './MediaDevices'
import useUserContextRef from './useUserContextRef'
import { ExternalEventEmitter } from '../ExternalEventEmitter'
import { ExternalEvent, AppContext, AppContextType } from '../AppContext'

export default function useMediaDeviceStatusService() {
    const { state: appState } = useContext(AppContext) as AppContextType
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const { audioInputsClassifiedForUi } = useMediaSourceViews()

    const updateMediaPreflightStatus = useCallback(
        (type: keyof MediaPreflightStatus, status: MediaPrefilghtStatusState) => {
            dispatch({
                type: 'update_media_preflightstatus',
                value: {
                    [type]: status,
                },
            })
        },
        []
    )

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName === 'get-mute') {
                const data: {
                    nativeDeviceId: number
                    errorCode: number
                    isMuted: number
                } = externalEvent.value

                // set state.mediaSourceStatus.audioInputs, matched by the nativeDeviceId.
                const audioInput = userStateRef.current.mediaSources.audioInputs.find(
                    (a) => a.nativeDeviceId === data.nativeDeviceId + ''
                )
                if (audioInput) {
                    const next = structuredClone(userStateRef.current.mediaSourceStatus.audioInputs)
                    next[audioInput.deviceId] = {
                        isMuted: !!data.isMuted,
                        localStreamStats: next[audioInput.deviceId] ? next[audioInput.deviceId].localStreamStats : null,
                    }

                    if (env.OS === 'darwin') {
                        let code = ''
                        if (isMuseDAWBridgeDevice(audioInput)) {
                            code = 'museDawBridge'
                        } else if (isMuseAudioShareInputDevice(audioInput)) {
                            code = 'museAudioShare'
                        } else if (isMuseRecordingDevice(audioInput)) {
                            code = 'museRecording'
                        }

                        if (code) {
                            if (!next[code]) {
                                next[code] = { isMuted: false, localStreamStats: null }
                            }
                            next[code].isMuted = !!data.isMuted
                        }
                    }

                    dispatch({
                        type: 'update_media_source_status',
                        value: { audioInputs: next },
                    })
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    useEffect(() => {
        const devices = state.mediaSources.audioInputs
            .filter((a) => isSystemAudio(a) && a.nativeDeviceId != null)
            .map((a) => ({ nativeDeviceId: a.nativeDeviceId || '', isInput: true }))
        ElectronIPC.getNativeDeviceMute({ devices: devices })
    }, [state.mediaSources.audioInputs, state.mediaSelection.audioInputs])

    useEffect(() => {
        classifyAudioInputs(state.mediaSources.audioInputs)

        const { museDawBridge, system: systemAudio, external: selectedExternalAudioInputs } = audioInputsClassifiedForUi

        // Muse DAW Bridge
        // =================================================
        ;(() => {
            if (!FeatureFlags.isEnabled('desktop-app-only-feature')) {
                updateMediaPreflightStatus('audioDaw', { isReady: null /* neutral */ })
                return
            }

            if (museDawBridge.length === 0) {
                updateMediaPreflightStatus('audioDaw', {
                    isReady: false,
                    errorCode: 'no_audio_driver',
                    message: 'No audio driver found.',
                })
                return
            }

            if (!museDawBridge.find((a) => state.mediaSelection.audioInputs.find((b) => b.deviceId === a.deviceId))) {
                updateMediaPreflightStatus('audioDaw', { isReady: null /* neutral */ })
                return
            }

            if (!appState.appState.musePluginState.isConnected) {
                updateMediaPreflightStatus('audioDaw', {
                    isReady: false,
                    errorCode: 'no_muse_plugin_detected',
                    message:
                        'Muse Plugin not detected. To share DAW Audio, add the Muse Plugin to the master track of your DAW.',
                })
                return
            }

            updateMediaPreflightStatus('audioDaw', { isReady: true })
        })()

        // System Audio
        // =================================================
        ;(() => {
            if (!FeatureFlags.isEnabled('desktop-app-only-feature')) {
                updateMediaPreflightStatus('audioComputer', { isReady: null /* neutral */ })
                return
            }

            if (systemAudio.length === 0) {
                updateMediaPreflightStatus('audioComputer', {
                    isReady: false,
                    errorCode: 'no_audio_driver',
                    message: 'No audio driver found.',
                })
                return
            }

            if (!systemAudio.find((a) => state.mediaSelection.audioInputs.find((b) => b.deviceId === a.deviceId))) {
                updateMediaPreflightStatus('audioComputer', { isReady: null /* neutral */ })
                return
            }

            if (state.mediaSelection.showSystemAudioOutputChangeConfirmation) {
                updateMediaPreflightStatus('audioComputer', {
                    isReady: false,
                    errorCode: 'global_audio_output_mismatch',
                    message: `You need to change your computer's audio output to Muse Audio Share to share computer audio.`,
                })
                return
            }

            updateMediaPreflightStatus('audioComputer', { isReady: true })
        })()

        // Audio Input (external)
        // =================================================
        ;(() => {
            if (!state.mediaPermissions.audio) {
                updateMediaPreflightStatus('audioInput', {
                    isReady: false,
                    errorCode: 'no_audio_permission',
                    message: 'Microphone permission not granted.',
                })
                return
            }

            if (selectedExternalAudioInputs.length > 0) {
                updateMediaPreflightStatus('audioInput', { isReady: true })
            } else {
                updateMediaPreflightStatus('audioInput', { isReady: null /* neutral */ })
            }
        })()

        // Audio Output (speakers)
        // =================================================
        ;(() => {
            if (isDefinedDeviceId(state.mediaSelection.audioOutput)) {
                updateMediaPreflightStatus('audioOutput', { isReady: true })
            } else {
                updateMediaPreflightStatus('audioOutput', { isReady: null /* neutral */ })
            }
        })()

        // MIDI
        // =================================================
        ;(() => {
            if (isDefinedDeviceId(state.mediaSelection.midiInput)) {
                updateMediaPreflightStatus('midi', { isReady: true })
            } else {
                updateMediaPreflightStatus('midi', { isReady: null /* neutral */ })
            }
        })()
    }, [
        state.mediaSources.audioInputs,
        state.mediaSelection.audioOutput,
        state.mediaSelection.audioInputs,
        state.mediaSelection.midiInput,
        appState.appState.musePluginState,
        state.mediaPermissions,
        state.mediaSelection.showSystemAudioOutputChangeConfirmation,
    ])
}
